import React from 'react';
import { IconButton, keyframes } from '@mui/material';

// Spinning animation for the generating state
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export type DownloadButtonState = 'default' | 'generating' | 'ready' | 'download' | 'error';

interface DownloadButtonProps {
  state: DownloadButtonState;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

const getButtonStyles = (state: DownloadButtonState) => {
  const baseStyles = {
    width: 28,
    height: 28,
    color: '#FFF',
    border: '1px solid',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.0)',
    },
  };

  switch (state) {
    case 'default':
      return {
        ...baseStyles,
        borderColor: '#676774',
        '&:hover': {
          ...baseStyles['&:hover'],
          borderColor: '#676774',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
      };
    case 'generating':
      return {
        ...baseStyles,
        borderColor: '#3870FF',
        cursor: 'default',
        '& svg': {
          animation: `${spin} 1s linear infinite`,
        },
        '&:hover': {
          transform: 'none',
        },
      };
    case 'ready':
      return {
        ...baseStyles,
        borderColor: '#12B76A',
        '&:hover': {
          ...baseStyles['&:hover'],
          borderColor: '#12B76A',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
        },
      };
    case 'download':
      return {
        ...baseStyles,
        borderColor: '#12B76A',
        '&:hover': {
          ...baseStyles['&:hover'],
          borderColor: '#12B76A',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
        },
      };
    case 'error':
      return {
        ...baseStyles,
        borderColor: '#F04438',
        '&:hover': {
          ...baseStyles['&:hover'],
          borderColor: '#F04438',
          backgroundColor: 'rgba(244, 67, 54, 0.1)',
        },
      };
    default:
      return baseStyles;
  }
};

const getIcon = (state: DownloadButtonState) => {
  switch (state) {
    case 'default':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_4637_10928)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.99967 1.45825C7.16076 1.45825 7.29134 1.58884 7.29134 1.74992L7.29134 11.5458L11.1684 7.66868C11.2823 7.55478 11.467 7.55478 11.5809 7.66868C11.6948 7.78258 11.6948 7.96725 11.5809 8.08116L7.20591 12.4562C7.09201 12.5701 6.90734 12.5701 6.79344 12.4562L2.41844 8.08116C2.30453 7.96726 2.30453 7.78258 2.41843 7.66868C2.53234 7.55478 2.71701 7.55478 2.83091 7.66868L6.70801 11.5458L6.70801 1.74992C6.70801 1.58884 6.83859 1.45825 6.99967 1.45825Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_4637_10928">
              <rect width="14" height="14" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case 'generating':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
        >
          <path
            d="M12.0671 6.23352C12.067 7.46538 11.677 8.66561 10.9528 9.66218C10.2287 10.6587 9.20773 11.4005 8.03614 11.7811C6.86456 12.1618 5.60255 12.1617 4.43099 11.781C3.25943 11.4003 2.23846 10.6585 1.5144 9.66191C0.79035 8.6653 0.400382 7.46506 0.400391 6.23319C0.4004 5.00133 0.790385 3.80109 1.51445 2.80449C2.23852 1.8079 3.2595 1.0661 4.43107 0.685423C5.60264 0.304746 6.86465 0.304728 8.03622 0.685374"
            stroke="white"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'ready':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M11.739 3.45098C11.8731 3.24994 11.8187 2.97831 11.6177 2.84428C11.4166 2.71025 11.145 2.76457 11.011 2.96562L6.057 10.3966L2.93436 7.27394C2.7635 7.10308 2.4865 7.10308 2.31564 7.27394C2.14479 7.44479 2.14479 7.7218 2.31564 7.89266L5.81564 11.3927C5.90832 11.4853 6.03768 11.5316 6.16811 11.5187C6.29854 11.5058 6.41632 11.435 6.48902 11.326L11.739 3.45098Z"
            fill="white"
          />
        </svg>
      );
    case 'download':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.99967 1.45825C7.16076 1.45825 7.29134 1.58884 7.29134 1.74992L7.29134 11.5458L11.1684 7.66868C11.2823 7.55478 11.467 7.55478 11.5809 7.66868C11.6948 7.78258 11.6948 7.96725 11.5809 8.08116L7.20591 12.4562C7.09201 12.5701 6.90734 12.5701 6.79344 12.4562L2.41844 8.08116C2.30453 7.96726 2.30453 7.78258 2.41843 7.66868C2.53234 7.55478 2.71701 7.55478 2.83091 7.66868L6.70801 11.5458L6.70801 1.74992C6.70801 1.58884 6.83859 1.45825 6.99967 1.45825Z"
            fill="white"
          />
        </svg>
      );
    case 'error':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.29344 3.29368C3.40734 3.17978 3.59201 3.17978 3.70591 3.29368L6.99967 6.58744L10.2934 3.29368C10.4073 3.17978 10.592 3.17978 10.7059 3.29368C10.8198 3.40758 10.8198 3.59226 10.7059 3.70616L7.41215 6.99992L10.7059 10.2937C10.8198 10.4076 10.8198 10.5923 10.7059 10.7062C10.592 10.8201 10.4073 10.8201 10.2934 10.7062L6.99967 7.4124L3.70591 10.7062C3.59201 10.8201 3.40734 10.8201 3.29344 10.7062C3.17953 10.5923 3.17953 10.4076 3.29344 10.2937L6.5872 6.99992L3.29344 3.70616C3.17953 3.59226 3.17953 3.40758 3.29344 3.29368Z"
            fill="white"
          />
        </svg>
      );
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.99967 1.45825C7.16076 1.45825 7.29134 1.58884 7.29134 1.74992L7.29134 11.5458L11.1684 7.66868C11.2823 7.55478 11.467 7.55478 11.5809 7.66868C11.6948 7.78258 11.6948 7.96725 11.5809 8.08116L7.20591 12.4562C7.09201 12.5701 6.90734 12.5701 6.79344 12.4562L2.41844 8.08116C2.30453 7.96726 2.30453 7.78258 2.41843 7.66868C2.53234 7.55478 2.71701 7.55478 2.83091 7.66868L6.70801 11.5458L6.70801 1.74992C6.70801 1.58884 6.83859 1.45825 6.99967 1.45825Z"
            fill="white"
          />
        </svg>
      );
  }
};

export const CameraDownloadIcon: React.FC<DownloadButtonProps> = ({
  state,
  onClick,
  size = 'medium',
  disabled = false,
}) => {
  const handleClick = () => {
    if (state !== 'generating' && !disabled && onClick) {
      onClick();
    }
  };

  return (
    <IconButton
      onClick={handleClick}
      disabled={state === 'generating' || disabled}
      size={size}
      sx={getButtonStyles(state)}
    >
      {getIcon(state)}
    </IconButton>
  );
};

export default CameraDownloadIcon;
